import { Link } from 'react-router-dom'
import Seo from '@/components/Seo'
import SiteLayout from '@/components/SiteLayout'
import PageHero from '@/components/PageHero'
import FeatureSection from '@/components/FeatureSection'
import FeatureTabs from '@/components/FeatureTabs'
import CtaBand from '@/components/CtaBand'
import { Card, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { getRelatedFeatures } from '@/content/features'
import { cardImageOf, heroImageOf, sectionsWithImages } from '@/content/applyImages'
import { SITE_URL } from '@/components/Seo'

export default function ContentPage({
  path,
  seoTitle,
  seoDescription,
  heroTitle,
  heroDescription,
  heroImage,
  heroImageAlt,
  images,
  eyebrow,
  sections = [],
  sectionLayout = 'images',
  detailTabs,
  relatedFeatureSlugs = [],
  resourceLinks = [],
  jsonLd,
  ctaTitle,
  ctaDescription,
  ctaButtonLabel,
}) {
  const related = getRelatedFeatures(relatedFeatureSlugs)
  const page = { images, heroImage, sections }
  const heroSrc = heroImageOf(page) || heroImage
  const resolvedSections = sectionsWithImages(page)
  const useTabs = sectionLayout === 'tabs'
  const tabs = detailTabs?.tabs || sections.map((section) => ({
    label: section.shortTitle || section.title,
    title: section.title,
    description: section.description,
    bullets: section.bullets,
    body: section.body,
  }))
  const tabsHeading = detailTabs?.title || 'How it works'
  const tabsDescription = detailTabs?.description

  const defaultJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: seoTitle,
    description: seoDescription,
    url: `${SITE_URL}${path}`,
    isPartOf: {
      '@type': 'WebSite',
      name: 'Tillio POS',
      url: SITE_URL,
    },
  }

  return (
    <SiteLayout>
      <Seo
        title={seoTitle}
        description={seoDescription}
        path={path}
        image={heroSrc}
        jsonLd={jsonLd || defaultJsonLd}
      />
      <PageHero
        title={heroTitle}
        description={heroDescription}
        image={heroSrc}
        imageAlt={heroImageAlt}
        eyebrow={eyebrow}
      />
      {useTabs ? (
        <FeatureTabs
          title={tabsHeading}
          description={tabsDescription}
          tabs={tabs}
        />
      ) : (
        resolvedSections.map((section, idx) => (
          <FeatureSection
            key={section.title}
            title={section.title}
            description={section.description}
            image={section.image}
            imageAlt={section.imageAlt}
            bullets={section.bullets}
            reverse={idx % 2 === 1}
            className={idx % 2 === 0 ? 'bg-background' : 'bg-slate-50'}
          />
        ))
      )}
      {resourceLinks.length > 0 && (
        <section className="py-12 bg-slate-50">
          <div className="container mx-auto max-w-7xl px-6">
            <h2 className="text-2xl font-bold text-center mb-6">Related resources</h2>
            <div className="flex flex-wrap justify-center gap-4">
              {resourceLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  className="text-primary font-medium hover:underline"
                >
                  {link.label}
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}
      {related.length > 0 && (
        <section className="py-16 bg-background">
          <div className="container mx-auto max-w-7xl px-6">
            <h2 className="text-3xl font-bold text-center mb-10">Related features</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {related.map((feature) => (
                <Link key={feature.slug} to={feature.path} className="block group">
                  <Card className="h-full overflow-hidden hover:shadow-lg transition-shadow">
                    <div className="h-40 overflow-hidden">
                      <img
                        src={cardImageOf(feature)}
                        alt={feature.title}
                        className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                      />
                    </div>
                    <CardHeader>
                      <CardTitle>{feature.title}</CardTitle>
                      <CardDescription>{feature.hubDescription}</CardDescription>
                    </CardHeader>
                  </Card>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}
      <CtaBand
        title={ctaTitle}
        description={ctaDescription}
        buttonLabel={ctaButtonLabel}
      />
    </SiteLayout>
  )
}
